import { useState } from "react";
import { Plus, Trash2, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { isVideoSrc, prepareMediaFile } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function GalleryManager({ items, reload }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [media, setMedia] = useState(null); // { dataUrl, mediaType }
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle("");
    setMedia(null);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const prepared = await prepareMediaFile(file);
      setMedia(prepared);
      if (prepared.mediaType === "video") toast.success("Video optimizado y listo");
    } catch (err) {
      toast.error(err?.message || formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!media) {
      toast.error("Sube una imagen o un video primero");
      return;
    }
    setSaving(true);
    try {
      await api.post("/gallery", {
        title: title.trim(),
        media_url: media.dataUrl,
        media_type: media.mediaType,
      });
      toast.success("Agregado a la galería");
      reset();
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Elemento eliminado");
      reload();
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div data-testid="gallery-panel">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-serif text-3xl">Galería de la Iglesia</h2>
        <Button
          data-testid="new-gallery-item-button"
          onClick={() => setOpen(true)}
          className="rounded-full bg-primary text-primary-foreground hover:bg-[#E5B54B]"
        >
          <Plus className="mr-2 h-4 w-4" /> Agregar Foto o Video
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-border bg-card sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Agregar a la Galería</DialogTitle>
            <DialogDescription>
              Sube una foto o video de tus eventos para mostrarlo en la página pública.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Título (opcional)</Label>
              <Input
                data-testid="gallery-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Vigilia de Adoración · Julio 2026"
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label>Foto o Video</Label>
              <div className="flex items-center gap-3">
                <label
                  htmlFor="gallery-file-input"
                  data-testid="gallery-file-label"
                  className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-primary/40 bg-muted px-4 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                  {uploading ? "Optimizando…" : media ? "Cambiar archivo" : "Subir imagen o video"}
                </label>
                <input
                  id="gallery-file-input"
                  type="file"
                  accept="image/*,video/*"
                  data-testid="gallery-file-input"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                {media && !uploading && (
                  <button
                    type="button"
                    onClick={() => setMedia(null)}
                    data-testid="gallery-file-remove"
                    className="text-xs text-muted-foreground hover:text-destructive"
                  >
                    Quitar
                  </button>
                )}
              </div>
              {media && (
                media.mediaType === "video" ? (
                  <video
                    src={media.dataUrl}
                    data-testid="gallery-media-preview"
                    className="mt-3 max-h-48 rounded-sm border border-border"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img
                    src={media.dataUrl}
                    alt="Vista previa"
                    data-testid="gallery-media-preview"
                    className="mt-3 max-h-48 rounded-sm border border-border object-contain"
                  />
                )
              )}
              <p className="text-[10px] text-muted-foreground">
                Los videos se optimizan automáticamente para cargar rápido en celulares (máx. 25 MB).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={submit}
              disabled={saving || uploading}
              data-testid="save-gallery-item-button"
              className="rounded-full bg-primary text-primary-foreground hover:bg-[#E5B54B]"
            >
              {saving ? "Guardando…" : "Agregar a la Galería"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <Card className="border-dashed border-border bg-card p-16 text-center text-muted-foreground">
          La galería está vacía. Agrega la primera foto o video con el botón de arriba.
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <Card
              key={item.id}
              data-testid={`gallery-item-${item.id}`}
              className="group overflow-hidden border-border bg-card p-0 transition-colors hover:border-primary/50"
            >
              {item.media_type === "video" || isVideoSrc(item.media_url) ? (
                <video
                  src={item.media_url}
                  className="h-44 w-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              ) : (
                <img src={item.media_url} alt={item.title || "Galería"} className="h-44 w-full object-cover" />
              )}
              <div className="flex items-center justify-between gap-3 p-4">
                <span className="truncate text-sm text-muted-foreground">
                  {item.title || "Sin título"}
                </span>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      data-testid={`delete-gallery-${item.id}`}
                      className="shrink-0 text-muted-foreground transition-colors hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="border-border bg-card">
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar de la galería?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta foto o video dejará de mostrarse en la página pública. Esta acción no se puede deshacer.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => remove(item.id)}
                        data-testid={`confirm-delete-gallery-${item.id}`}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Eliminar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
